/* eslint-disable react/no-multi-comp */
import loadable from '@loadable/component';
import React, { Component, PureComponent } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import { routerShape } from 'react-router';
import { JsonLd } from 'react-schemaorg';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import each from 'lodash/each';
import get from 'lodash/get';
import filter from 'lodash/filter';
import map from 'lodash/map';
import Bem from 'bemHelper';
import { I18nHoc } from 'helpers/i18n';
import absoluteUrl from 'helpers/absoluteUrl';
import capitalizeFirstLetter from 'helpers/capitalizeFirstLetter';
import DataProvider from 'api/data-provider';
import pageCache from 'api/page-cache';
import preload from 'api/data-preload';
import { simpleCheck } from 'api/data-check';
import seoPreload, { seoDecorator } from 'api/seo';
import Counter from 'subsys/counter';
import GeoLandings from 'subsys/geo-landings';
import AskQuestion from 'subsys/ask-question';
import { marketStatEvents, mstatDecorator } from 'subsys/market-stat';
import contentBlock, { blockPreloader } from 'containers/content-block';
import sensor from 'components/sensor';
import Container from 'components/container';
import TabJSControl from 'components/tab-js-control';
import { offsetHelper } from 'components/paginator';
import Breadcrumbs from 'components/breadcrumbs';
import { SeoText } from 'components/layouts';
import  { iconTypes } from 'components/icon';
import { changeDevice } from 'components/view-map-search/view-search';

import linksDomik from 'src-sites/domik/links';
import blockConfig from 'src-sites/domik/blocks/config';
import BlockWrapper from 'src-sites/domik/blocks/block-wrapper';

import ContentProject from './content-project';
import {
  ifToShowAskQuestion,
  renderSalerPhone,
  renderContactToDeveloper
} from './render-common';
import { checkSold, renderMainPrice } from './cards-object/list-card';

import marker from '../assets/map-marker';

import translates from './project-i18n';

import 'styles/domik/section-project/_page-project.scss';

const ContentObjects = loadable(() => import(/* webpackPrefetch: true */ './content-objects'));
const ContentObject = loadable(() => import(/* webpackPrefetch: true */ './content-object'));
const ContentNews = loadable(() => import(/* webpackPrefetch: true */ './content-news'));
const ContentContacts = loadable(() => import(/* webpackPrefetch: true */ './content-contacts'));

const bemClasses = new Bem('project');
const OBJECTS_PER_PAGE = 16;

@preload((routeProps) => {
  const { content, landing } = routeProps.params;
  let config = {
    ...pageCache('project'),
    project: [DataProvider.preload, {
      url: 'domik/location/newbuild',
      cache: 60,
      requestClientCache: 1,
      query: { id: landing.params.complex.id }
    }],
    childLandings: [DataProvider.preload, {
      url: 'bild/complex-child-landings',
      cache: 60,
      requestClientCache: 1,
      query: {
        branch: routeProps.params.landing.tree,
        entityNot: 'object'
      }
    }],
    landing: [DataProvider.preload, {
      url: 'bild/complex-child-landings',
      cache: 60,
      requestClientCache: 1,
      query: { id: routeProps.params.landing.id }
    }],
    creators: {
      $wait: ['project'],
      $callback: (data) => {
        const cfg = {};
        const ids = get(data, 'project.data[0].creator_ids', []);
        const mainId = get(data, 'project.data[0].salers[0].parent');

        if (ids && !ids.includes(mainId)) ids.push(mainId);

        cfg.creatorList = ids ? [DataProvider.preload, {
          url: 'domik/users/developers-list',
          query: { ids },
          queryJson: true,
          cache: 60,
          requestClientCache: 1
        }] : null;
        return cfg;
      }
    },
    main_developer: {
      $wait: ['project'],
      $callback: (data) => {
        const cfg = {};
        const main_developer = get(data, 'project.data[0].salers[0].parent');
        if (main_developer) {
          cfg.main_developer = [DataProvider.preload, {
            url: 'domik/users/developer',
            query: {
              id: main_developer
            },
            requestClientCache: 1
          }]
        }
        return cfg;
      }
    },
    terms: [DataProvider.preload, {
      url: 'bild/control-readiness',
      queryJson: true,
      cache: 60,
      requestClientCache: 1,
      query: { location_id: landing.params.complex.id }
    }],
    ...blockPreloader({
      project: 'domik',
      entity: 'complexPage'
    }, blockConfig, ['project', 'landing'])
  };

  if (content === 'objects') {
    const pageId = `complexObjects_${landing.data.dict}_${landing.data.value}`;
    config = {
      ...config,
      objects: [DataProvider.preload, {
        url: 'domik/objects/newbuild-objects',
        queryJson: true,
        cache: 60,
        requestClientCache: 1,
        query: {
          complex_id: landing.params.complex.id,
          count: true,
          priceCurrency: 'uah',
          // limit: OBJECTS_PER_PAGE,
          offset: offsetHelper(routeProps.params.page, OBJECTS_PER_PAGE),
          ...landing.params.obj
        }
      }],
      ...simpleCheck(['project', 'objects']),
      ...seoPreload({ entity: pageId }, ['project', 'childLandings', 'landing', 'main_developer'])
    };
  } else if (content === 'project') {
    config = {
      ...config,
      nearestLC: {
        $wait: ['project'],
        $callback: (data) => {
          const cfg = {};
          const geoJson = get(data, 'project.data[0].geo_centroid');
          const type = get(data, 'project.data[0].type');
          if (!geoJson) return {};
          geoJson.parameters = { distance: 10000 };
          cfg.nearestLC = [DataProvider.preload, {
            url: 'domik/location/newbuilds-list',
            queryJson: true,
            cache: 60,
            requestClientCache: 1,
            query: {
              geoDWithin: geoJson,
              limit: 4,
              idNot: landing.params.complex.id,
              distanceColumn: geoJson,
              distanceOrder: 'asc',
              type,
              unsold: true,
              notFrozenStatus: true,
              notSalesSuspendedUntil: true,
              notProjectCanceledStatus: true
            }
          }];
          return cfg;
        }
      },
      anchors: [DataProvider.preload, {
        url: 'domik/anchors/anchors',
        queryJson: true,
        cache: 60,
        requestClientCache: 1,
        query: {
          livingComplexId: landing.params.complex.id,
          count: true
        }
      }],
      ...simpleCheck(['project']),
      ...seoPreload({ entity: 'complexPage' }, ['project', 'childLandings', 'landing', 'main_developer'])
    };
  } else if (content === 'object') {
    config = {
      ...config,
      object: [DataProvider.preload, {
        url: 'domik/objects/newbuild-objects',
        queryJson: true,
        cache: 60,
        requestClientCache: 1,
        query: {
          id: landing.params.object.id,
          priceCurrency: 'uah'
        }
      }],
      otherObj: {
        $wait: ['object'],
        $callback: (data) => {
          const cfg = {};
          const classes_number =
            get(data, 'object.data[0].classesDict.id') ||
            get(data, 'object.data[0].classesDict[0].id');
          const type = get(data, 'object.data[0].type_eng');
          let classes = typeof classes_number === 'object' ?
            classes_number :
            [classes_number];
          classes = type !== 'saleMarketplace' ? classes : null;

          cfg.otherObj = [DataProvider.preload, {
            url: 'domik/objects/newbuild-objects',
            query: {
              complex_id: landing.params.complex.id,
              idNot: landing.params.object.id,
              classes,
              type,
              priceCurrency: 'uah'
            },
            queryJson: true,
            cache: 60,
            requestClientCache: 1
          }];
          return cfg;
        }
      },
      ...simpleCheck(['project']),
      ...seoPreload({ entity: 'complexObject' }, ['project', 'childLandings', 'landing', 'object', 'main_developer'])
    };
  } else if (content === 'complexNews') {
    config = {
      ...config,
      complexNews: {
        $wait: ['project'],
        $callback: (data) => {
          const cfg = {};
          const creatorIds = get(data, 'project.data[0].creator_ids') || [];
          const livingComplexId = landing.params.complex.id;
          const query = {
            order: ['dateTrunc', 'desc'],
            count: true
          }
          if (creatorIds.length) {
            query.lcIdOrDevelopers = [livingComplexId, creatorIds];
          } else {
            query.livingComplexId = livingComplexId;
          }
          cfg.complexNews = [DataProvider.preload, {
            url: 'domik/news/publication-list',
            queryJson: true,
            cache: 60,
            requestClientCache: 1,
            query
          }];
          return cfg;
        }
      },
      ...simpleCheck(['project']),
      ...seoPreload({ entity: 'complexNews' }, ['project', 'childLandings', 'landing', 'main_developer'])
    };
  } else if (content === 'complexContacts') {
    config = {
      ...config,
      complexContacts: {
        $wait: ['project'],
        $callback: (data) => {
          const cfg = {};
          const salerId = get(data, 'project.data[0].salers[0].id');
          if (salerId) {
            cfg.complexContacts = [DataProvider.preload, {
              url: 'domik/users/departments-list',
              query: {
                id: salerId
              },
              requestClientCache: 1
            }]
          }
          return cfg;
        }
      },
      ...simpleCheck(['project']),
      ...seoPreload({ entity: 'complexContacts' }, ['project', 'childLandings', 'landing', 'main_developer'])
    };
  }

  return config;
})
@seoDecorator({
  advancedVars: {
    projBreadcrumb: 'Хлебная крошка проекта',
    endBreadcrumb: 'Хлебная крошка страницы',
    plansHeader: 'Заголовок планировок',
    variantsHeader: 'Заголовок вариантов покупки',
    optsHeader: 'Заголовок характеристик',
    otherObjHeader: 'Заголовок "Ещё" текущего типа объекта',
    objectsTypeHeader: 'Заголовок планировок по типам недвижимости',
    bottomText: 'Текст в самом низу',
    imgAlt1: 'Alt картинки 1',
    imgAlt2: 'Alt картинки 2',
    imgAlt3: 'Alt картинки 3',
    imgAlt4: 'Alt картинки 4',
    imgAlt5: 'Alt картинки 5',
    imgAlt6: 'Alt картинки 6',
    imgAlt7: 'Alt картинки 7',
    imgAlt8: 'Alt картинки 8',
    imgAlt9: 'Alt картинки 9',
    imgAlt10: 'Alt картинки 10'
  }
})
@mstatDecorator(marketStatEvents.historyChange, (props) => {
  const { content, url } = props.params;
  switch (content) {
    case 'project':
      return { entity: 'Карта ЖК/КГ', entityId: get(props, 'project.data[0].id') };
    case 'objects':
      return { entity: 'Планировки ЖК/КГ', entityId: get(props, 'project.data[0].id'), url };
    case 'object':
      return { entity: 'Объект ЖК/КГ', entityId: get(props, 'object.data[0].id') };
    default:
      return null;
  }
})
@sensor
@I18nHoc(translates())
export default class PageProject extends Component {
  static propTypes = {
    router: routerShape,
    params: PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      pageType: PropTypes.string,
      project: PropTypes.string,
      content: PropTypes.string
    }),
    objects: PropTypes.shape({}),
    location: PropTypes.shape({}),
    childLandings: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    project: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    seoData: PropTypes.shape({}),
    landing: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    creators: PropTypes.shape({}),
    object: PropTypes.shape({}),
    otherObj: PropTypes.shape({}),
    nearestLC: PropTypes.shape({}),
    anchors: PropTypes.shape({}),
    terms: PropTypes.shape({}),
    mainMenuBase: PropTypes.shape({})
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.project = 'domik';

    this.onHandleResize = this.onHandleResize.bind(this);
    this.renderAskQuestion = this.renderAskQuestion.bind(this);
    this.seoObject = this.seoObject.bind(this);
    this.onResize = this.onHandleResize;
    this.numberPM = 0;
    this.numberCom = 0;
    this.isScrollToMap = get(props, 'router.location.hash') === '#map';

    this.state = {
      device: 'desktop'
    };
  }

  componentDidMount() {
    this.onHandleResize(this.sensor.getRuntime());
  }

  onHandleResize({ media: { desktop, tablet, phone } = {} }) {
    changeDevice.call(this, desktop, tablet, phone);
  }

  getSortedChildLandings() {
    const { childLandings } = this.props;
    return sortBy(childLandings.data, val =>
      (val.data && val.data.value) ? (val.data.value.sort || val.data.value.sort_order || -1) : -1
    );
  }

  getData = () => {
    const { seoData, creators } = this.props;
    const data = this.getSortedChildLandings();
    const objGroup = filter(data, land => land.level === 2);
    const creatorsData = get(creators, 'creatorList.data');
    const idProject = get(this, 'props.project.data[0].id');
    const displayName = get(this, 'props.project.data[0].display_name');
    const topic = seoData && seoData.header ? seoData.header : displayName;
    const url = get(this, 'props.params.landing.url');
    const tel = get(this, 'props.project.data[0].salers[0].phones[0]');
    const complexSite = get(this, 'props.project.data[0].advanced_info.complex_sites.val[0]') || get(this, 'props.project.data[0].advanced_info.complex_sites[0]', null);
    const complexForum = get(this, 'props.project.data[0].advanced_info.complex_forums.val[0]') || get(this, 'props.project.data[0].advanced_info.complex_forums[0]', null);

    return {
      objGroup,
      idProject,
      displayName,
      topic,
      url,
      tel,
      complexSite,
      complexForum,
      creatorsData,
      seoData
    };
  };

  seoObject() {
    const { childLandings, project = {} } = this.props;
    const { all_flats_sold, all_commercial_sold, all_house_and_area_sold } = project.data[0];
    const soldData = { all_flats_sold, all_commercial_sold, all_house_and_area_sold };
    const childLandingsData = sortBy(childLandings.data, val =>
      (val.data && val.data.value) ? (val.data.value.sort || val.data.value.sort_order || -1) : -1
    );

    let idx = -1;
    let seoObjectData = [];

    each(childLandingsData, land => {
      if (land.subgroup === 'objGrp') {
        idx += 1;
        const { data } = land;
        const { groupId } = data;
        const filterChildLandings = childLandingsData.filter(
          n => n.data.groupId === groupId && n.level !== 2 && n.subgroup
        );
        const filterChildData = map(filterChildLandings, item => {
          const { data = {}, all_flats_sold_object, $entity } = item;
          const { group, allSold } = data;
          const groupType = group || $entity;
          const sold = checkSold.call(this, all_flats_sold_object || allSold, soldData, groupType);

          return renderMainPrice.call(this, item.data, bemClasses, true, sold.sold, true);
        });
        const filterChildDataNoNull = filterChildData.filter(element => element !== null && element !== undefined);

        seoObjectData = [...seoObjectData, ...filterChildDataNoNull];
      }
    });

    return seoObjectData;
  }

  renderAdvertBlock =(name)=> {
    return contentBlock(blockConfig, name, this.props, { wrapper: BlockWrapper, classNameWrapper: bemClasses('blockPProjAdvert', null, 'blockAdvert').className, withContainer: false, location: get(this, 'props.location') })
  }

  renderGeoWrapper = (contentName) => {
    const { mainMenuBase = {} } = this.props;
    return(
      <GeoLandings base={mainMenuBase} injectPropName="landingLinks" project="domik">
        <GeoLandingsWrapper {...this.props} contentName={contentName} />
      </GeoLandings>
    );
  }

  _renderProject() {
    const {
      project, params = {}, nearestLC, anchors, terms , main_developer
    } = this.props;
    const mainDeveloperData = get(main_developer, 'main_developer.data.[0]');
    const {
      objGroup, idProject,
      topic, url, tel, complexSite, complexForum,
      seoData, creatorsData
    } = this.getData();
    const { landing } = params;
    const projectData = project.data[0];
    const {
      geo_centroid: geo, problem_desc, reliability_for_whom, description,
      address: streetAddress, locality_display_name: addressLocality,
      images: { others } = {}
    } = projectData;

    return (
      <>
        <Counter
          entity={{ project_id: idProject }}
          mode="view"
          action="pageProjectView"
        />
        <ContentProject
          project={project.data[0]}
          objGroup={objGroup}
          seoData={seoData}
          topic={topic}
          childLandings={this.getSortedChildLandings()}
          creators={creatorsData}
          mainDeveloperData={mainDeveloperData}
          nearestLC={nearestLC}
          anchors={anchors}
          renderAskQuestion={this.renderAskQuestion}
          landing={landing}
          terms={get(terms, 'data')}
          isSensor={this.sensor}
          isScrollToMap={this.isScrollToMap}
          renderAdvertBlock={this.renderAdvertBlock}
          projBeforeObj={this.renderGeoWrapper('projBeforeObj')}
          projAfterObj={this.renderGeoWrapper('projAfterObj')}
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ApartmentComplex',
            name: topic,
            url: absoluteUrl(url),
            description: [problem_desc, reliability_for_whom, description],
            image: map(others, img => absoluteUrl(`/images/thumb/full/${img.id}`)),
            address: {
              '@type': 'PostalAddress',
              streetAddress,
              addressLocality
            },
            geo: get(geo, 'type') === 'Point' ? {
              '@type': 'GeoCoordinates',
              latitude: geo.coordinates[1],
              longitude: geo.coordinates[0]
            } : null,
            telephone: tel,
            sameAs: [complexSite, complexForum]
          }}
        />
        {this.seoObject().length > 0 &&
          <JsonLd
            item={{
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: topic,
              url: absoluteUrl(url),
              description: [problem_desc, reliability_for_whom, description],
              image: map(others, img => absoluteUrl(`/images/thumb/full/${img.id}`)),
              sameAs: [complexSite, complexForum],
              offers: this.seoObject()
            }}
          />
        }
      </>
    );
  }

  _renderComplexNews() {
    const { project, complexNews = {}, params } = this.props;
    const { topic, seoData, idProject } = this.getData();
    const newsContentBlocks = (
      <>
        {contentBlock(blockConfig, 'news1', this.props, { wrapper: BlockWrapper, classNameWrapper: bemClasses('blockPProjNews1', null, 'projNews').className, withContainer: false })}
        {contentBlock(blockConfig, 'news2', this.props, { wrapper: BlockWrapper, classNameWrapper: bemClasses('blockPProjNews2', null, 'projNews').className, withContainer: false })}
        {contentBlock(blockConfig, 'news3', this.props, { wrapper: BlockWrapper, classNameWrapper: bemClasses('blockPProjNews3', null, 'projNews').className, withContainer: false })}
      </>
    );

    return (
      <>
        <Counter
          entity={{
            project_id: idProject
          }}
          mode="view"
          action="pageNewsProjectView"
        />
        <ContentNews
          project={project.data[0]}
          preloadedNews={complexNews.complexNews}
          params={params}
          seoData={seoData}
          topic={topic}
          rightColumn={newsContentBlocks}
          renderAskQuestion={this.renderAskQuestion}
        />
      </>
    );
  }

  _renderComplexContact() {
    const { project, complexContacts, params } = this.props;
    const mainDeveloperData = get(this, 'props.main_developer.main_developer.data.[0]');
    const { topic, seoData, idProject } = this.getData();
    const contentBlocksContacts = (
      <>
        {contentBlock(blockConfig, 'contacts1', this.props, { withContainer: false })}
        {contentBlock(blockConfig, 'contacts2', this.props, { withContainer: false })}
        {contentBlock(blockConfig, 'contacts3', this.props, { withContainer: false })}
      </>
    );

    return (
      <>
        <Counter
          entity={{
            project_id: idProject
          }}
          mode="view"
          action="pageContactsProjectView"
        />
        <ContentContacts
          project={project.data[0]}
          preloadedContacts={complexContacts}
          params={params}
          seoData={seoData}
          topic={topic}
          contentBlocks={contentBlocksContacts}
          mainDeveloperData={mainDeveloperData}
        />
      </>
    );
  }

  renderAskQuestion(id, project, url) {
    const { i18n } = this;
    const showAskQuestion = ifToShowAskQuestion(project);
    const mainDeveloperData = get(this, 'props.main_developer.main_developer.data.[0]');
    const firstSeller = {...mainDeveloperData, ...get(project,'salers[0]')}

    if (showAskQuestion) {
      return (
        <AskQuestion
          {...bemClasses('blockAskQuestion')}
          ref={ref => this.askQuestionRef = ref}
          controller="support/ask-question/ask-developer-question"
          entityId={id}
          entity="ask_question"
          url={url}
          otherProps={{
            notificationMessageSuccess: i18n('askQuestionMessage'),
            inputPhoneProps: { noLabel: true, noElementBefore: true },
            additionalMain: false,
            headerTopic: true,
            headerTopicText: i18n('askQuestionTopic'),
            firstSeller: firstSeller,
            hideTelephoneBtnIconType: iconTypes.phoneCalls,
            marker,
            counterProps: {
              action: 'askDeveloperQuestion',
              entity: {
                project_id: id
              }
            }
          }}
        />
      );
    }

    return false;
  }

  renderNavi() {
    const { landing } = this.props;
    const childTabs = [];
    const data = this.getSortedChildLandings();
    let selectedTab = 0;
    let idx = 0;

    each(data, (land, key) => {
      if (land.level === 1 || land.level === 2) {
        if (landing.data[0].data.groupId === land.data.groupId) selectedTab = idx;
        idx += 1;

        const name = land.level === 1 ? this.i18n('tabMain') : land.data.value.sectionName;

        childTabs.push(
          <tab
            key={key}
            id={land.id}
            title={name}
            linkProps={{
              builder: linksDomik.sectionLanding,
              builderProps: {
                ...land,
                name
              }
            }}
          />
        );
      }
    });

    return (
      <TabJSControl
        {...bemClasses('tab')}
        selectedTab={selectedTab}
        linkMode
      >
        {childTabs}
      </TabJSControl>
    );
  }

  // TODO: REFACTOR THIS!
  //  Split 1001 if and megatons of code to separate functions, like as _renderTerms()!!!
  renderContent() {
    const {
      project, objects, landing,
      router, params = {},
      object, otherObj, main_developer
    } = this.props;
    const mainDeveloperData = get(main_developer, 'main_developer.data.[0]');
    const { content } = params;
    const {
      objGroup, idProject, displayName,
      topic, url, tel, complexSite, complexForum,
      seoData, creatorsData
    } = this.getData();
    const projectData = project.data[0];
    const {
      geo_centroid: geo, address: streetAddress, locality_display_name: addressLocality
    } = projectData;
    const jsonApartmentComplex = (
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          '@type': 'ApartmentComplex',
          name: topic,
          url: absoluteUrl(url),
          address: {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality
          },
          geo: get(geo, 'type') === 'Point' ? {
            '@type': 'GeoCoordinates',
            latitude: geo.coordinates[1],
            longitude: geo.coordinates[0]
          } : null,
          telephone: tel,
          sameAs: [complexSite, complexForum]
        }}
      />
    );

    // *** Project content renderer
    if (content === 'project') {
      return this._renderProject();
    } else if (content === 'objects' && objects) { // *** Objects content renderer
      return (
        <>
          <Counter
            //Просмотр страницы объектов в ЖК уже есть
            entity={{
              project_id: idProject
            }}
            mode="view"
            action="pageObjectsView"
          />
          <ContentObjects
            project={project.data[0]}
            objectsPreloaded={objects}
            landing={landing.data[0]}
            objGroup={objGroup}
            creators={creatorsData}
            mainDeveloperData={mainDeveloperData}
            renderAskQuestion={this.renderAskQuestion}
            router={router}
            params={params}
            seoData={seoData}
            topic={topic}
          />
          {jsonApartmentComplex}
        </>
      );
    } else if (content === 'object') { // *** Objects content renderer
      const objData = get(this, 'props.object.data[0]');
      const idObject = get(objData, 'id');
      const type = get(project, 'data[0].type');
      const images = get(objData, 'images');
      const dict = get(objData, 'classesDict.dict');
      const apartmentValue = get(objData, 'classesDict.combo') || get(objData, 'classesDict.value_long') || get(objData, 'classesDict.entity_long', '');
      const apartmentType = 'ROM';
      const areaTxt = get(objData, 'object_properties.all_area') || get(objData, 'object_properties.land_area') * 100 || '';
      const areaType = 'MTK';

      return (
        <>
          <Counter
            //Просмотр страницы объектА в ЖК уже есть
            entity={{
              object_id: idObject,
              project_id: idProject
            }}
            mode="view"
            action="pageObjectView"
          />
          <ContentObject
            project={project.data[0]}
            preloadedObj={object}
            otherObj={otherObj}
            creators={creatorsData}
            mainDeveloperData={mainDeveloperData}
            landing={landing.data[0]}
            objGroup={objGroup}
            renderAskQuestion={this.renderAskQuestion}
            router={router}
            params={params}
            isSensor={this.sensor}
            seoData={seoData}
            topic={topic}
          />
          <JsonLd
            item={{
              '@context': 'https://schema.org',
              '@type': 'Apartment',
              name: topic,
              url: absoluteUrl(url),
              image: map(images, img => absoluteUrl(`/images/thumb/full/${img.id}`)),
              numberOfRooms: (type === 'living_complex' && dict === 'rooms') ? {
                '@type': 'QuantitativeValue',
                value: apartmentValue,
                unitCode: apartmentType
              } : null,
              floorSize: {
                '@type': 'QuantitativeValue',
                value: areaTxt,
                unitCode: areaType
              },
              telephone: tel,
              sameAs: [complexSite, complexForum]
            }}
          />
          {jsonApartmentComplex}
        </>
      );
    } else if (content === 'complexNews') {
      return this._renderComplexNews();
    } else if (content === 'complexContacts') {
      return this._renderComplexContact();
    }
  }

  render() {
    const { i18n } = this;
    const { device } = this.state;
    const {
      mainMenuBase = {}, seoData, params: { landing, content }
      // , project = {}, creators, childLandings, object
    } = this.props;
    // const creatorsData = get(creators, 'creatorList.data');
    // const projectData = get(project, 'data[0]');
    const {
      // url: landingUrl,
      breadcrumbs
    } = landing;
    // const { id, display_name = '' } = projectData;
    // const seoPropsTile = { itemScope: ' ', itemType: content === 'object' ? 'https://schema.org/Apartment' : 'https://schema.org/ApartmentComplex' };
    const seoText = get(seoData, 'bottomText');
    const { endBreadcrumb, projBreadcrumb } = seoData;
    // const topic = get(seoData, 'header') || display_name;
    // const childLand = find(childLandings.data, { id: landing.id });
    // const backUrl = getBackUrl();
    // const complexTree = get(projectData, 'tree');
    // const url = get(this, 'props.location.pathname', '');
    // const { id } = project.data[0];
    const isDesktop = device === 'desktop';
    // const isPhone = device === 'phone';

    return (
      <Container body {...bemClasses('pageContent')}>
        <div {...bemClasses('toBack')}>
          {/*backUrl &&
            <Link
              {...bemClasses('link', 'toBack')}
              {...backUrl}
            >
              <span {...bemClasses('linkContent')}>
                <Icon {...bemClasses('icon')} type={iconTypes.arrowSliderLeft} />
                <span {...bemClasses('text')} children={this.i18n('linkToBackText')} />
              </span>
            </Link>
          */}
        </div>
        {/*
        <div {...bemClasses('title')}>
          <div {...bemClasses('text')}><h1 children={topic} /></div>
        </div>
        */}
        <div {...bemClasses('tabContainer', 'main sticky')} children={this.renderNavi()} />
        {/*isPhone && renderComplaint.call(this, id, url, bemClasses)*/}
        {/*<div {...bemClasses('tabContent')}>*/}
          {this.renderContent()}
          {!isDesktop &&
            <div {...bemClasses('panel', 'fixed')}>
              {renderSalerPhone.call(this, bemClasses)}
              {renderContactToDeveloper.call(this, bemClasses, i18n('connectConsultShort'))}
            </div>
          }
          <LazyHydrate whenVisible>
            <GeoLandings base={mainMenuBase} injectPropName="landingLinks" project="domik">
              <>
                {contentBlock(blockConfig, 'projBottom1', this.props, { mainMenuBase, wrapper: BlockWrapper, classNameWrapper: bemClasses('stocksLC', null, 'blockPProject1').className })}
                {contentBlock(blockConfig, 'projBottom2', this.props, { classNameWrapper: 'blockPProjectBottom2' })}
                {contentBlock(blockConfig, 'projBottom3', this.props, { classNameWrapper: 'blockPProjectBottom3' })}
              </>
            </GeoLandings>
          </LazyHydrate>
        {/*</div>*/}
        <Breadcrumbs
          data={breadcrumbs}
          replace={{
            $newbuild: projBreadcrumb, // projectData.display_name,
            $objects: endBreadcrumb,
            // $objects:
            // get(childLand, 'data.value.sectionName') ||
            // get(childLand, 'data.value.value_long'),
            $object: endBreadcrumb,
            $complexNews: endBreadcrumb,
            $complexContacts: endBreadcrumb
            // get(object, 'data[0].header') ||
            // get(object, 'data[0].type')
          }}
        />
        <SeoText data={seoText} />
      </Container>
    );
  }
}

class GeoLandingsWrapper extends PureComponent {
  static propTypes = {
    landingLinks: PropTypes.shape({}),
    geolocation: PropTypes.shape({}),
    contentName: PropTypes.string
  };

  render() {
    const { landingLinks, geolocation, contentName } = this.props;

    return (
      <LazyHydrate whenVisible>
        {contentBlock(blockConfig, contentName, this.props, { geolocation, landingLinks, wrapper: BlockWrapper, classNameWrapper: bemClasses(`blockP${capitalizeFirstLetter(contentName)}`, null, contentName).className, withContainer: false })}
      </LazyHydrate>
    );
  }
}

